import type { ModuleMigrationDef } from "@kubdee/sdk";

// ============================================================================
// 003_multi_vehicle_booking_lines
//
// Introduce normalized vehicle lines while keeping rental_bookings.vehicle_id
// as the backward-compatible primary vehicle. Current business rule:
// all vehicles in a booking share the same pickup/return schedule and
// booking-level workflow.
// ============================================================================

export const migration: ModuleMigrationDef = {
  key: "003_multi_vehicle_booking_lines",
  version: "1.2.0",
  sql: `
CREATE TABLE IF NOT EXISTS \`car_rental_booking_vehicles\` (
  \`id\` text PRIMARY KEY NOT NULL,
  \`company_id\` text NOT NULL,
  \`booking_id\` text NOT NULL,
  \`vehicle_id\` text NOT NULL,
  \`sort_order\` integer DEFAULT 0 NOT NULL,
  \`calculation_method\` text DEFAULT 'daily' NOT NULL,
  \`rental_days\` integer DEFAULT 1 NOT NULL,
  \`daily_rate\` real DEFAULT 0 NOT NULL,
  \`rental_amount\` real DEFAULT 0 NOT NULL,
  \`security_deposit\` real DEFAULT 0,
  \`driver_rate_per_day\` real DEFAULT 0,
  \`total_driver_cost\` real DEFAULT 0,
  \`status\` text DEFAULT 'active' NOT NULL,
  \`created_at\` text NOT NULL,
  \`created_by\` text,
  \`updated_at\` text NOT NULL,
  \`updated_by\` text,
  \`deleted_at\` text,
  \`deleted_by\` text,
  FOREIGN KEY (\`booking_id\`) REFERENCES \`car_rental_rental_bookings\`(\`id\`) ON UPDATE no action ON DELETE no action,
  FOREIGN KEY (\`vehicle_id\`) REFERENCES \`car_rental_vehicles\`(\`id\`) ON UPDATE no action ON DELETE no action
);
CREATE INDEX IF NOT EXISTS \`bv_company_booking_idx\` ON \`car_rental_booking_vehicles\` (\`company_id\`,\`booking_id\`);
CREATE INDEX IF NOT EXISTS \`bv_company_vehicle_idx\` ON \`car_rental_booking_vehicles\` (\`company_id\`,\`vehicle_id\`);
CREATE INDEX IF NOT EXISTS \`bv_booking_vehicle_idx\` ON \`car_rental_booking_vehicles\` (\`booking_id\`,\`vehicle_id\`);

CREATE TABLE IF NOT EXISTS \`car_rental_pickup_vehicle_lines\` (
  \`id\` text PRIMARY KEY NOT NULL,
  \`company_id\` text NOT NULL,
  \`pickup_id\` text NOT NULL,
  \`booking_id\` text NOT NULL,
  \`booking_vehicle_id\` text,
  \`vehicle_id\` text NOT NULL,
  \`sort_order\` integer DEFAULT 0 NOT NULL,
  \`mileage_out\` real,
  \`fuel_level_out\` text,
  \`vehicle_condition\` text,
  \`existing_damage_notes\` text,
  \`pickup_remarks\` text,
  \`created_at\` text NOT NULL,
  \`created_by\` text,
  \`updated_at\` text NOT NULL,
  \`updated_by\` text,
  \`deleted_at\` text,
  \`deleted_by\` text,
  FOREIGN KEY (\`pickup_id\`) REFERENCES \`car_rental_rental_pickups\`(\`id\`) ON UPDATE no action ON DELETE no action,
  FOREIGN KEY (\`booking_id\`) REFERENCES \`car_rental_rental_bookings\`(\`id\`) ON UPDATE no action ON DELETE no action,
  FOREIGN KEY (\`booking_vehicle_id\`) REFERENCES \`car_rental_booking_vehicles\`(\`id\`) ON UPDATE no action ON DELETE no action,
  FOREIGN KEY (\`vehicle_id\`) REFERENCES \`car_rental_vehicles\`(\`id\`) ON UPDATE no action ON DELETE no action
);
CREATE INDEX IF NOT EXISTS \`pvl_company_pickup_idx\` ON \`car_rental_pickup_vehicle_lines\` (\`company_id\`,\`pickup_id\`);
CREATE INDEX IF NOT EXISTS \`pvl_company_booking_idx\` ON \`car_rental_pickup_vehicle_lines\` (\`company_id\`,\`booking_id\`);
CREATE INDEX IF NOT EXISTS \`pvl_company_vehicle_idx\` ON \`car_rental_pickup_vehicle_lines\` (\`company_id\`,\`vehicle_id\`);

CREATE TABLE IF NOT EXISTS \`car_rental_return_vehicle_lines\` (
  \`id\` text PRIMARY KEY NOT NULL,
  \`company_id\` text NOT NULL,
  \`return_id\` text NOT NULL,
  \`booking_id\` text NOT NULL,
  \`booking_vehicle_id\` text,
  \`pickup_vehicle_line_id\` text,
  \`vehicle_id\` text NOT NULL,
  \`sort_order\` integer DEFAULT 0 NOT NULL,
  \`odometer_reading\` real,
  \`fuel_level\` text,
  \`exterior_condition\` text,
  \`interior_condition\` text,
  \`return_remarks\` text,
  \`created_at\` text NOT NULL,
  \`created_by\` text,
  \`updated_at\` text NOT NULL,
  \`updated_by\` text,
  \`deleted_at\` text,
  \`deleted_by\` text,
  FOREIGN KEY (\`return_id\`) REFERENCES \`car_rental_rental_returns\`(\`id\`) ON UPDATE no action ON DELETE no action,
  FOREIGN KEY (\`booking_id\`) REFERENCES \`car_rental_rental_bookings\`(\`id\`) ON UPDATE no action ON DELETE no action,
  FOREIGN KEY (\`booking_vehicle_id\`) REFERENCES \`car_rental_booking_vehicles\`(\`id\`) ON UPDATE no action ON DELETE no action,
  FOREIGN KEY (\`pickup_vehicle_line_id\`) REFERENCES \`car_rental_pickup_vehicle_lines\`(\`id\`) ON UPDATE no action ON DELETE no action,
  FOREIGN KEY (\`vehicle_id\`) REFERENCES \`car_rental_vehicles\`(\`id\`) ON UPDATE no action ON DELETE no action
);
CREATE INDEX IF NOT EXISTS \`rvl_company_return_idx\` ON \`car_rental_return_vehicle_lines\` (\`company_id\`,\`return_id\`);
CREATE INDEX IF NOT EXISTS \`rvl_company_booking_idx\` ON \`car_rental_return_vehicle_lines\` (\`company_id\`,\`booking_id\`);
CREATE INDEX IF NOT EXISTS \`rvl_company_vehicle_idx\` ON \`car_rental_return_vehicle_lines\` (\`company_id\`,\`vehicle_id\`);

INSERT INTO \`car_rental_booking_vehicles\` (
  \`id\`, \`company_id\`, \`booking_id\`, \`vehicle_id\`, \`sort_order\`,
  \`calculation_method\`, \`rental_days\`, \`daily_rate\`, \`rental_amount\`,
  \`security_deposit\`, \`driver_rate_per_day\`, \`total_driver_cost\`, \`status\`,
  \`created_at\`, \`created_by\`, \`updated_at\`, \`updated_by\`
)
SELECT
  'bv_' || b.\`id\`,
  b.\`company_id\`,
  b.\`id\`,
  b.\`vehicle_id\`,
  0,
  b.\`calculation_method\`,
  b.\`rental_days\`,
  b.\`daily_rate\`,
  b.\`rental_amount\`,
  b.\`security_deposit\`,
  b.\`driver_rate_per_day\`,
  b.\`total_driver_cost\`,
  CASE WHEN b.\`workflow_state\` = 'cancelled' THEN 'cancelled' ELSE 'active' END,
  b.\`created_at\`,
  b.\`created_by\`,
  b.\`updated_at\`,
  b.\`updated_by\`
FROM \`car_rental_rental_bookings\` b
WHERE NOT EXISTS (
  SELECT 1 FROM \`car_rental_booking_vehicles\` bv
  WHERE bv.\`booking_id\` = b.\`id\`
);

INSERT INTO \`car_rental_pickup_vehicle_lines\` (
  \`id\`, \`company_id\`, \`pickup_id\`, \`booking_id\`, \`booking_vehicle_id\`, \`vehicle_id\`,
  \`sort_order\`, \`mileage_out\`, \`fuel_level_out\`, \`vehicle_condition\`,
  \`existing_damage_notes\`, \`pickup_remarks\`, \`created_at\`, \`created_by\`,
  \`updated_at\`, \`updated_by\`
)
SELECT
  'pvl_' || p.\`id\`,
  b.\`company_id\`,
  p.\`id\`,
  p.\`booking_id\`,
  bv.\`id\`,
  b.\`vehicle_id\`,
  0,
  p.\`mileage_out\`,
  p.\`fuel_level_out\`,
  p.\`vehicle_condition\`,
  p.\`existing_damage_notes\`,
  p.\`pickup_remarks\`,
  p.\`created_at\`,
  p.\`created_by\`,
  p.\`updated_at\`,
  p.\`updated_by\`
FROM \`car_rental_rental_pickups\` p
INNER JOIN \`car_rental_rental_bookings\` b ON b.\`id\` = p.\`booking_id\`
LEFT JOIN \`car_rental_booking_vehicles\` bv ON bv.\`booking_id\` = b.\`id\` AND bv.\`vehicle_id\` = b.\`vehicle_id\`
WHERE NOT EXISTS (
  SELECT 1 FROM \`car_rental_pickup_vehicle_lines\` pvl
  WHERE pvl.\`pickup_id\` = p.\`id\`
);

INSERT INTO \`car_rental_return_vehicle_lines\` (
  \`id\`, \`company_id\`, \`return_id\`, \`booking_id\`, \`booking_vehicle_id\`,
  \`pickup_vehicle_line_id\`, \`vehicle_id\`, \`sort_order\`, \`odometer_reading\`,
  \`fuel_level\`, \`exterior_condition\`, \`interior_condition\`, \`return_remarks\`,
  \`created_at\`, \`created_by\`, \`updated_at\`, \`updated_by\`
)
SELECT
  'rvl_' || r.\`id\`,
  b.\`company_id\`,
  r.\`id\`,
  r.\`booking_id\`,
  bv.\`id\`,
  pvl.\`id\`,
  b.\`vehicle_id\`,
  0,
  r.\`odometer_reading\`,
  r.\`fuel_level\`,
  r.\`exterior_condition\`,
  r.\`interior_condition\`,
  r.\`return_remarks\`,
  r.\`created_at\`,
  r.\`created_by\`,
  r.\`updated_at\`,
  r.\`updated_by\`
FROM \`car_rental_rental_returns\` r
INNER JOIN \`car_rental_rental_bookings\` b ON b.\`id\` = r.\`booking_id\`
LEFT JOIN \`car_rental_booking_vehicles\` bv ON bv.\`booking_id\` = b.\`id\` AND bv.\`vehicle_id\` = b.\`vehicle_id\`
LEFT JOIN \`car_rental_pickup_vehicle_lines\` pvl ON pvl.\`pickup_id\` = r.\`pickup_id\` AND pvl.\`vehicle_id\` = b.\`vehicle_id\`
WHERE NOT EXISTS (
  SELECT 1 FROM \`car_rental_return_vehicle_lines\` rvl
  WHERE rvl.\`return_id\` = r.\`id\`
);
`.trim(),
};
